import { z } from 'zod';

import { AuthenticatorId, LoginData, extractTokenVersionFromJwt } from 'backend-client-web';
import {
  loadRawEncryptionPublicKey,
  RawEncryptionPublicKey,
  RawEncryptionPrivateKey,
  loadRawEncryptionPrivateKey,
  EncryptionPublicKeySignature,
  loadEncryptionPublicKeySignature,
  RawSymAesEncryptionKey,
  loadRawSymAesEncryptionKey,
} from 'vault-crypto';

import { brandedUint8Array } from '../brands';

import { PersistableUnlockedAuthenticator } from './authenticator/PersistableUnlockedAuthenticator';

/**
 * WARNING! The ClientCoreParameters defined in this file can only be changed in backwards
 * compatible ways!
 *
 * We create ClientCoreParameters for a user on account creation (for authenticators) or pairing
 * (for other sessions). After that, we never re-write them anywhere. This means that any new
 * version needs to be able to deal with all old versions of ClientCoreParameters that were every
 * around in the wild.
 *
 * This is very sensitive. If ClientCoreParameters are unreadable users will be unable to use their
 * authenticators and may lose access to their logins completely.
 */

// Raw schema for parsing (tokenVersion is optional from persisted data)
const schemaLoginDataRaw = z.object({
  userId: z.uuid(),
  sessionId: z.uuid(),
  token: z.string(),
  tokenVersion: z.number().optional(),
});

// Transformed schema that ensures tokenVersion is always present. Use explicit tokenVersion if
// present, otherwise extract from JWT header.
// TODO, simplify this and make tokenVersion mandatory once we no longer support v3 tokens. At that
// point we'll know that there are no longer any CCP without a tokenVersion in the wild. All CCP
// with at least v4 tokens will have a tokenVersion from the beginning, as the field was introduced
// before v4 tokens.
const schemaLoginData = schemaLoginDataRaw.transform(
  (data): LoginData => ({
    userId: data.userId,
    sessionId: data.sessionId,
    token: data.token,
    tokenVersion: data.tokenVersion ?? extractTokenVersionFromJwt(data.token),
  }),
);

interface PersistableSessionKeys {
  encPrivKey: RawEncryptionPrivateKey;
  encPubKey: RawEncryptionPublicKey;
  encPubKeySignature: EncryptionPublicKeySignature;
  signingAuthId: AuthenticatorId;
  persistableEncryptionKey?: RawSymAesEncryptionKey;
}

const PersistableSessionKeys = z.object({
  encPrivKey: brandedUint8Array(loadRawEncryptionPrivateKey),
  encPubKey: brandedUint8Array(loadRawEncryptionPublicKey),
  encPubKeySignature: brandedUint8Array(loadEncryptionPublicKeySignature),
  signingAuthId: z.uuid(),
  persistableEncryptionKey: brandedUint8Array(loadRawSymAesEncryptionKey).optional(),
});

interface ClientCoreParameters {
  readonly loginData: LoginData;
  readonly unlockedAuthenticator: PersistableUnlockedAuthenticator;
  readonly sessionKeys: PersistableSessionKeys;
}

const ClientCoreParameters = z.object({
  loginData: schemaLoginData,
  unlockedAuthenticator: PersistableUnlockedAuthenticator,
  sessionKeys: PersistableSessionKeys,
});

export { ClientCoreParameters, PersistableSessionKeys };
